import { useEffect, useState, useRef, useLayoutEffect } from "react"
import { useParams } from "react-router-dom"
import { toast } from "@stardust-ds/react"
import intl from "react-intl-universal"
import "./styles.css"
import { analyteLaunchSkip } from "../../../../../../../services/ciq"
import useDataApi from "../../../../../../../hooks/useDataApi"
import { useAnalyte } from "../../context/analyte"

const CHART_DATA_LIMIT = 10
const CHART_SURFACE_CLASS = "recharts-surface"
const CHART_GRID_CLASS = "recharts-cartesian-grid-horizontal"
const WESTGARD_EACTION = {
  A: "A",
  R: "R",
}

// set at least 10 aRuns in chart data
const setChartDataMinumumRegister = (chartData) => {
  let fakeRunNumber = -1
  while (chartData?.length < CHART_DATA_LIMIT) {
    // add mock aRuns to fill the chart
    chartData.push({ run: fakeRunNumber, mockLine: true })
    fakeRunNumber--
  }
  return chartData
}

export function useChartLevereyJenning({
  aRuns,
  setSelectedRun,
  pagination = false,
  setIsOpen,
  anlt_nid,
}) {
  const [selectedChartRun, setSelectedChartRun] = useState(null) // run number selected in chart
  const [chartDataPage, setChartDataPage] = useState(1)
  const [chartData, setChartData] = useState([])
  const [runsWestardStatus, setRunsWestardStatus] = useState([]) // runs with westgard status
  const [bLineAnimationActive, setBLineAnimationActive] = useState(true)
  const [nSpaceBetweenVerticalLines, setNSpaceBetweenVerticalLines] = useState(16)
  const chartWrapperRef = useRef(null)
  const { id: equipmentId } = useParams()
  const [oFetchRunRes, fnFetchRunReq] = useDataApi(analyteLaunchSkip)
  const analyteContext = useAnalyte()

  const dataAnalyte = analyteContext?.launchAnalysis
  const setDataAnalyte = analyteContext?.setLaunchAnalysis

  // filter chart data by chartDataPage
  const filterChartData = () => {
    if (!chartData.length) {
      // return 10 mock runs
      return setChartDataMinumumRegister([])
    }

    const aFilteredChartData = chartData
      .sort((a, b) => a.run - b.run)
      .slice(
        chartDataPage - 1,
        chartData.length - 1 >= CHART_DATA_LIMIT
          ? CHART_DATA_LIMIT + (chartDataPage - 1)
          : CHART_DATA_LIMIT,
      )

    return aFilteredChartData?.length < CHART_DATA_LIMIT
      ? setChartDataMinumumRegister(aFilteredChartData)
      : aFilteredChartData
  }

  const chartDataLength = filterChartData()?.length
  const bCanLess = chartData.length > CHART_DATA_LIMIT && chartDataPage > 1 // check if has previous runs to show
  const bCanMore =
    chartData.length > CHART_DATA_LIMIT &&
    CHART_DATA_LIMIT + (chartDataPage - 1) < chartData.length // check if has more runs to show
  const hasMoreData =
    chartData.length >= CHART_DATA_LIMIT && filterChartData()?.[0]?.run !== 1 // check if has more run to fetch

  useEffect(() => {
    if (!aRuns?.length) {
      setChartData([])
      return
    }

    const newChartData = mountChartData(aRuns)
    setChartData(newChartData)

    if (newChartData.length > CHART_DATA_LIMIT) {
      setChartDataPage(newChartData.length - (CHART_DATA_LIMIT - 1))
    }

    if (!selectedChartRun && pagination) {
      const lastRun = newChartData.reduce(
        (run, chartRun) => (chartRun.run > run ? chartRun.run : run),
        1,
      )
      setSelectedChartRun(lastRun)
    }
  }, [aRuns])

  useEffect(() => {
    if (oFetchRunRes.loading) return

    if (oFetchRunRes.error)
      toast({
        type: "error",
        title: intl.get("ciq.loadAnalyteRunError.title"),
        description: intl.get("ciq.loadAnalyteRunError.description"),
      })
  }, [oFetchRunRes])

  useLayoutEffect(() => {
    if (!filterChartData()?.length) return

    const observer = new ResizeObserver(() => {
      setSpaceBetweenVerticalLines()
    })

    if (chartWrapperRef?.current) {
      observer.observe(chartWrapperRef.current)
      setTimeout(() => {
        setSpaceBetweenVerticalLines()
      }, 10)
    }

    return () => {
      observer.disconnect()
    }
  }, [chartWrapperRef, filterChartData()])

  useEffect(() => {
    if (!aRuns?.length) return
    const selectedRun = aRuns.find((run) => run.eqpanltrun_number === selectedChartRun)
    setSelectedRun?.(selectedRun || null)
  }, [selectedChartRun])

  // convert anlt_runs (runs prop) array to chart data
  const mountChartData = (aRuns) => {
    if (!aRuns?.length) return []

    let chartDataAux = []
    // set chart data for each run
    for (let index = 0; index < aRuns.length; index++) {
      let lines = {}
      const run = aRuns[index]

      // set the lines for each control level
      for (let i = 0; i < run?.eqpanltrun_launches?.length; i++) {
        const launch = run?.eqpanltrun_launches[i]
        setRunWestgardStatus(run, launch)

        const prevChartRun = index <= 0 ? null : chartDataAux[index - 1]
        const prevLaunch =
          index <= 0
            ? null
            : aRuns[index - 1]?.eqpanltrun_launches?.find(
                (equi) => equi?.eqpanltlaunch_elevel === launch?.eqpanltlaunch_elevel,
              )
        const nLaunchLevel = launch.eqpanltlaunch_elevel

        const nLine = !prevChartRun
          ? 1
          : prevLaunch?.eqpanltlaunch_naverage === launch?.eqpanltlaunch_naverage &&
            prevLaunch?.eqpanltlaunch_nstandard_deviation ===
              launch?.eqpanltlaunch_nstandard_deviation
          ? prevChartRun?.[nLaunchLevel] || 1
          : (prevChartRun?.[nLaunchLevel] || 0) + 1

        const lineDotPosition =
          typeof launch?.eqpanltlaunch_ndeviation === "number"
            ? launch.eqpanltlaunch_ndeviation
            : null

        lines = {
          ...lines,
          [`level-${nLaunchLevel}-line-${nLine}`]: lineDotPosition,
          [nLaunchLevel]: nLine,
        }
      }

      chartDataAux.push({
        run: run?.eqpanltrun_number,
        ...lines,
      })
    }

    return chartDataAux
  }

  // get chart grid position and size properties
  const getChartGridProperties = () => {
    if (!chartWrapperRef?.current) return

    const elSvgSurface =
      chartWrapperRef.current.getElementsByClassName(CHART_SURFACE_CLASS)[0]
    if (!elSvgSurface) return

    const elGrid = elSvgSurface.getElementsByClassName(CHART_GRID_CLASS)[0]
    if (!elGrid) return

    const { width, left } = elSvgSurface.getBoundingClientRect()
    const {
      left: leftGrid,
      top: topGrid,
      height: heightGrid,
    } = elGrid.getBoundingClientRect()

    return { width, left, leftGrid, topGrid, heightGrid }
  }

  // set the space value between vertical lines in chart grid
  const setSpaceBetweenVerticalLines = () => {
    if (!filterChartData()?.length) return

    const chartGridProperties = getChartGridProperties()
    if (!chartGridProperties) return

    const { width, left, leftGrid } = chartGridProperties
    const nSpaceValue = (width - (leftGrid - left)) / filterChartData().length
    setNSpaceBetweenVerticalLines(nSpaceValue)
  }

  const fetchRun = async (runNumber) => {
    return await fnFetchRunReq(equipmentId, anlt_nid, runNumber, "prev")
  }

  // group runs by westgard status
  const setRunWestgardStatus = (run, launch) => {
    if (launch?.eqpanltlaunch_awestgard_rules?.length) {
      for (let i = 0; i < launch.eqpanltlaunch_awestgard_rules.length; i++) {
        const westgard = launch.eqpanltlaunch_awestgard_rules[i]
        if (WESTGARD_EACTION[westgard?.eqpanltlnchwgr_eaction]) {
          const oNewWestgard = {
            run: run?.eqpanltrun_number,
            eaction: westgard.eqpanltlnchwgr_eaction,
          }
          setRunsWestardStatus((prev) => [...prev, oNewWestgard])
        }
      }
    }
  }

  const handleOnClickChart = (entry, event) => {
    // when there is no run, check if click is in edge of chart
    let selectedRunNumber = entry?.activeLabel
    if (!selectedRunNumber) {
      const chartGridProperties = getChartGridProperties()
      if (!chartGridProperties) return

      const { width, left, leftGrid, topGrid, heightGrid } = chartGridProperties
      const { clientX, clientY } = event

      // check if click was inside the chart on the x-axis
      if (clientX < leftGrid || clientX - left > width) return
      // check if click was inside the chart on the y-axis
      if (clientY < topGrid || clientY - topGrid > heightGrid) return

      // find which edge was clicked
      const closedEdgte = clientX - left < width / 2 ? "left" : "right"

      // select first run in chart
      if (closedEdgte === "left") {
        const firstRun = filterChartData()?.[0]?.run
        // verify if fisrt run is valid
        if (firstRun > 0) selectedRunNumber = firstRun
      }

      // select last run in chart
      if (closedEdgte === "right") {
        const lastRun = filterChartData()?.[chartDataLength - 1]?.run
        // verify if last run is valid
        if (lastRun > 0) selectedRunNumber = lastRun
      }
    }

    if (selectedRunNumber < 0) return
    if (selectedRunNumber === selectedChartRun) {
      setIsOpen(true)
      return
    }

    setSelectedChartRun(selectedRunNumber)
  }

  const handleClickMoveGraph = async (viewer) => {
    setBLineAnimationActive(false) // disable chart animation on paginate

    if (viewer === "more" && bCanMore) {
      setChartDataPage((oldstate) => oldstate + 1)
      return
    }

    if (viewer === "less") {
      if (bCanLess) {
        setChartDataPage((oldstate) => oldstate - 1)
        return
      }

      if (hasMoreData) {
        const lastRun = filterChartData()?.[0].run
        const response = await fetchRun(lastRun)
        if (!response?.data) return

        const newRun = mountChartData([response.data])
        addRunToAnalyte(response.data)
        setChartData((prev) => [...newRun, ...prev])
        setChartDataPage(1)
        return
      }
    }
  }

  const addRunToAnalyte = (run) => {
    // update analyte state from release index
    const dataAnalyteAux = [...dataAnalyte]

    const newAnalyte = dataAnalyteAux.map((analyte) => {
      if (analyte.anlt_nid === anlt_nid) {
        const newAnalyte = { ...analyte }
        newAnalyte.anlt_runs.unshift(run)
        return newAnalyte
      }

      return analyte
    })

    setDataAnalyte(newAnalyte)
  }

  return {
    chartData: filterChartData(),
    runsWestardStatus,
    nSpaceBetweenVerticalLines,
    bLineAnimationActive,
    handleOnClickChart,
    handleClickMoveGraph,
    oFetchRunRes,
    chartWrapperRef,
    hasMoreData,
    bCanLess,
    bCanMore,
    selectedChartRun,
  }
}
